import { DateTime } from 'luxon';
import { IST_ZONE } from './slot.util';
import { toTwelveHour } from './timeFormat.util';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  THE TITLE WRITES ITSELF, AND IT IS NEVER STORED.
 *
 *  ⚠️ A STORED DERIVED TITLE GOES STALE THE MOMENT SOMEONE EDITS THE LOCATION, and then
 *  describes somewhere the arrangement is not. So `arrangements.title` is NULL whenever
 *  the operator did not type one, and every reader derives instead — the intake's live
 *  greyed placeholder, the list row, the detail header and the printed order.
 *
 *  ⚠️ THAT IS FOUR READERS ON TWO SIDES OF THE WIRE, WHICH IS WHY THIS IS SHARED.
 *  The API calls it in `toArrangementDto` to fill `displayTitle`; the web calls it while
 *  the operator types, before there is anything to send. Same input, same string. The
 *  alternative — the API returning the parts for the web to format — is two
 *  implementations of one sentence, and they diverge on the plural.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

export type TitleParts = {
  /** Ordered as the operator entered them. Only the first is named. */
  locationNames: readonly string[];
  /** `YYYY-MM-DD`, the PA's own date in IST. */
  paDate: string;
  /** `HH:mm` of the first shift, or null when no time is fixed. */
  firstStartTime?: string | null | undefined;
};

/**
 * `'2026-08-15'` → `'15 Aug 2026'`. Never a numeric-only date: 08/09 is two dates.
 *
 * ⚠️ [amended D4.2] EXPORTED, BECAUSE THE APP HAD NO DATE FORMATTER TO IMPORT.
 *
 * It was `const`, so "use the app's existing formatter" was not a thing anyone could do —
 * and the result was that the duty point card printed a raw `2026-08-16` directly under a
 * picker showing `16 Aug 2026`. Making the existing one importable is strictly better than
 * writing a second: this is the function whose output already appears in every derived
 * arrangement title, so a summary and its own title now agree by construction.
 *
 * ⚠️ TWO SPELLINGS EXIST AND THIS IS THE PROSE ONE. Pickers pass `dd LLL yyyy`
 * (zero-padded) as a `format` prop; this is `d LLL yyyy`. They differ only on a
 * single-digit day, and unifying them means changing what a picker displays across seven
 * screens — a bigger change than a layout pass should make silently.
 */
export const readableDate = (paDate: string): string | null => {
  const day = DateTime.fromISO(paDate, { zone: IST_ZONE });
  return day.isValid ? day.toFormat('d LLL yyyy') : null;
};

/**
 * `'Esplanade on 15 Aug 2026 at 20:00'`.
 *
 * Returns `null` when there is not yet enough to say anything true — no place, or an
 * unparseable date. The caller decides what an empty title looks like, because the intake
 * wants a prompt there and the list wants the PA's reference number.
 *
 * ⚠️ THE EXTRA PLACES ARE COUNTED, NOT LISTED. "Esplanade and 2 other places" stays one
 * line at any width; joining four names does not, and a title that wraps to three lines
 * in a table is what makes an operator start typing their own.
 */
export const deriveArrangementTitle = (parts: TitleParts): string | null => {
  const named = parts.locationNames.map((name) => name.trim()).filter((name) => name.length > 0);
  const first = named[0];
  const date = readableDate(parts.paDate);
  if (first === undefined || date === null) return null;

  const others = named.length - 1;
  const where = others === 0 ? first : `${first} and ${String(others)} other place${others === 1 ? '' : 's'}`;
  // [P4-UX.2] Displayed to a human, so 12-hour like every other time in the UI.
  const at = parts.firstStartTime ? ` at ${toTwelveHour(parts.firstStartTime)}` : '';

  return `${where} on ${date}${at}`;
};

/**
 * What every reader actually asks: the operator's title if they typed one, otherwise the
 * derived one, otherwise the reference.
 *
 * ⚠️ A TRIMMED-EMPTY STORED TITLE IS TREATED AS ABSENT. The intake reverts to the derived
 * title when the field is cleared, and "cleared" has to mean the same thing to a reader
 * as it does to the writer — otherwise a title of one space renders as a blank row.
 */
export const arrangementDisplayTitle = (
  storedTitle: string | null | undefined,
  parts: TitleParts,
  reference: string,
): string => storedTitle?.trim() || deriveArrangementTitle(parts) || reference;
